import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ShortenedSnapshotsWithOverrides, SyncResponse } from '@/api/main/types.gen'

interface Props {
  opened: boolean
  onClose: () => void
  snapshots: ShortenedSnapshotsWithOverrides[]
  existingSchemaNames: string[]
  isSubmitting: boolean
  onSubmit: (snapshotName: string, schemaName: string) => Promise<SyncResponse[] | undefined>
}

const SCHEMA_NAME_PATTERN = /^[a-z0-9_]+$/

type Step = 'form' | 'confirm'

export function CreateSchemaModal({ opened, onClose, snapshots, existingSchemaNames, isSubmitting, onSubmit }: Props) {
  const { t } = useTranslation('mapSnapshots')
  const [step, setStep] = useState<Step>('form')

  const form = useForm({
    initialValues: {
      source_snapshot: snapshots[0]?.snapshot_name ?? '',
      schema_name: '',
    },
    validate: {
      source_snapshot: value => (value ? null : t('errSourceRequired')),
      schema_name: value => {
        if (!value) return t('errNameRequired')
        if (!SCHEMA_NAME_PATTERN.test(value)) return t('errNamePattern')
        if (existingSchemaNames.includes(value)) return t('errSchemaTaken')
        return null
      },
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({ source_snapshot: snapshots[0]?.snapshot_name ?? '', schema_name: '' })
      form.resetDirty()
      setStep('form')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened])

  const handleClose = () => {
    setStep('form')
    onClose()
  }

  return (
    <Modal opened={opened} onClose={handleClose} title={t('createSchema')} padding="lg" size="lg" yOffset="5vh">
      <Stack style={{ minHeight: 280 }}>
        {step === 'form' ? (
          <form onSubmit={form.onSubmit(() => setStep('confirm'))}>
            <Stack>
              <Select
                label={t('sourceSnapshot')}
                description={t('sourceSnapshotHelp')}
                data={snapshots.map(s => ({ value: s.snapshot_name, label: s.snapshot_name }))}
                searchable
                {...form.getInputProps('source_snapshot')}
                data-autofocus
              />
              <TextInput
                label={t('newSchemaName')}
                description={t('newSchemaNameHelp')}
                placeholder={t('destinationSchemaPlaceholder')}
                {...form.getInputProps('schema_name')}
              />
              <Group justify="flex-end" gap="xs">
                <Button variant="default" onClick={handleClose}>
                  {t('cancel')}
                </Button>
                <Button type="submit" color="blue" variant="light" disabled={!form.isDirty()}>
                  {t('createAndSync')}
                </Button>
              </Group>
            </Stack>
          </form>
        ) : (
          <Stack>
            <Text fw={600}>{t('confirmCreateSchemaTitle')}</Text>
            <Text size="sm">
              {t('confirmSyncSummary', {
                snapshot: form.values.source_snapshot,
                schema: form.values.schema_name,
              })}
            </Text>
            <Text size="sm" c="orange">
              {t('confirmCreateSchemaWarning')}
            </Text>
            <Group justify="flex-end" gap="xs" mt="md">
              <Button variant="default" onClick={() => setStep('form')}>
                {t('back')}
              </Button>
              <Button
                color="orange"
                loading={isSubmitting}
                onClick={() => onSubmit(form.values.source_snapshot, form.values.schema_name)}
              >
                {t('confirm')}
              </Button>
            </Group>
          </Stack>
        )}
      </Stack>
    </Modal>
  )
}
